import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCog, RefreshCw, ArrowLeft, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

/**
 * ImpersonationLogPage — compliance view of every time a master admin
 * "viewed as" another user. Read-only. Backed by
 * `GET /api/admin/impersonate/log` (master-admin-only).
 *
 * Useful when a support incident needs a paper trail of who could
 * possibly have seen a tenant's data — the underlying collection
 * `impersonation_log` is append-only, so this page is the definitive
 * source.
 */
const ImpersonationLogPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/impersonate/log?limit=${PAGE_SIZE}&skip=${skip}`)
      .then((r) => {
        setRows(r.data?.rows || []);
        setTotal(r.data?.total || 0);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load log'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [skip]);   // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            data-testid="impersonation-log-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="ml-4 flex items-center gap-2">
            <UserCog className="w-5 h-5 text-amber-600" />
            <h1 className="text-xl font-bold text-slate-900">Impersonation Audit Log</h1>
          </div>
          <button
            onClick={load}
            className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5"
            data-testid="impersonation-log-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        <p className="text-sm text-slate-500 mb-4 leading-snug">
          Every &ldquo;View as this user&rdquo; session that a master admin starts is recorded here — impersonator,
          target, target&apos;s role and domain, and the timestamp. Append-only compliance record.
        </p>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-5 h-5 text-slate-300 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-400 italic text-sm">
              No impersonation events recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="impersonation-log-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Started</th>
                  <th className="text-left px-4 py-2.5">Impersonator</th>
                  <th className="text-left px-4 py-2.5">Target</th>
                  <th className="text-left px-4 py-2.5">Role</th>
                  <th className="text-left px-4 py-2.5">Domain</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`impersonation-row-${r.id}`}>
                    <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmt(r.started_at)}</td>
                    <td className="px-4 py-2 text-slate-800 font-medium">{r.impersonator_email}</td>
                    <td className="px-4 py-2 text-slate-800">{r.target_email}</td>
                    <td className="px-4 py-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {(r.target_role || '').replace('domain_', '')}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-[11px]">
                      {(r.target_domain_id || '').slice(0, 8) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <p className="text-slate-500">
              Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                className="px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="impersonation-log-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={skip + PAGE_SIZE >= total}
                className="px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="impersonation-log-next"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpersonationLogPage;
